import { useState } from 'react';
import { Popover, Tooltip } from '@ets/design-system';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { useSelector, useDispatch } from '_common/hooks';
import EditorManager from 'Editor/services/EditorManager';
import ColorsMenu from 'Editor/components/ColorsMenu/ColorsMenu';
import { SplitToggle } from '_common/components';
import { CustomIcon } from '_common/lib/components';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { getFormattedColor } from 'assets/colors';
import { ReduxInterface } from 'Editor/services';

const FontColor = ({ isReadOnlyMode }: { isReadOnlyMode: boolean }) => {
  const dispatch = useDispatch();
  const fontColor = useSelector((state) => {
    const color = state.editor.toolbar.color;

    if (color) {
      if (typeof color === 'boolean') {
        return !color ? 'transparent' : '';
      } else {
        return color.replace(/ /g, '');
      }
    }

    return '';
  });

  const [documentColors, setDocumentColors] = useState<
    { r: string | number; g: string | number; b: string | number; rgb: string }[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);

  const onOpenColorsPopover = () => {
    const documentColors = EditorDOMUtils.getDocumentColors();
    if (documentColors) {
      setDocumentColors(documentColors);
    }
  };

  const applyFontColor = (color: string) => {
    EditorManager.getInstance().toggleSelectionStyle('color', color);
  };

  const onFontColorChange = (color: string) => {
    ReduxInterface.updateToolbarValues({
      color,
    });
    applyFontColor(color);
  };

  const onClearParagraphBackgroundColor = () => {
    ReduxInterface.updateToolbarValues({
      color: 'rgb(0, 0, 0)',
    });
    EditorManager.getInstance().removeSelectionStyle('color', null);
  };

  const openColorPickerModal = () => {
    dispatch(
      openAndUpdateModal({
        modal: 'ColorPickerModal',
        data: {
          action: 'toggleSelectionStyle',
          actionValues: { style: 'color' },
        },
      }),
    );
  };

  return (
    <Popover.Root placement="bottom-start" open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <Tooltip content="Color" testId="font-color">
          <div>
            <SplitToggle
              isToggled={false}
              isOpen={isOpen}
              disabled={isReadOnlyMode}
              testId="font-color"
              onClick={() => {
                applyFontColor(fontColor);
                setIsOpen(false);
              }}
              customIcon={
                <CustomIcon
                  name="FontColor"
                  color={fontColor ? getFormattedColor(fontColor) : '#6D6D82'}
                  size={24}
                />
              }
              onPopperClick={() => {
                setIsOpen(!isOpen);
                onOpenColorsPopover();
              }}
              margin="2px"
            />
          </div>
        </Tooltip>
      </Popover.Trigger>
      <Popover.Content testId="font-color">
        <ColorsMenu
          documentColors={documentColors}
          toggle={() => setIsOpen(false)}
          changeBackgroundColor={(color: string) => {
            onFontColorChange(color);
            setIsOpen(false);
          }}
          clearBackgroundColor={() => {
            onClearParagraphBackgroundColor();
            setIsOpen(false);
          }}
          selected={fontColor}
          setOpenColorPickerModal={openColorPickerModal}
          attribute={undefined}
        />
      </Popover.Content>
    </Popover.Root>
  );
};

export default FontColor;
